import type {RouteRecordRaw, RouteComponent} from 'vue-router';

const lazy = (fn: () => Promise<any>) => fn as () => Promise<RouteComponent>;
const routes = [
  {
    path: '/',
    redirect: '/dashboard',
  },
  {
    path: '/design-system',
    name: 'design-system',
    meta: {layout: 'empty'},
    component: lazy(() => import('@/components/pages/DesignSystemPage.vue')),
  },
  {
    // Animation marketing « Fygr devient Okimia » (explainer 16:9), servie en
    // iframe depuis /public/presentation. Route dédiée dans le layout par
    // défaut (sidebar visible, contenu qui se scale comme les autres pages).
    path: '/presentation',
    name: 'presentation',
    component: lazy(() => import('@/components/pages/PresentationPage.vue')),
  },
  {
    // Fygr becomes Okimia announcement page. On the legacy app.fygr.io host this
    // is the only reachable page (see router.ts lock); elsewhere it is a normal
    // public page.
    path: '/fygr-becomes-okimia',
    name: 'fygr-becomes-okimia',
    meta: {layout: 'no-navigation'},
    component: lazy(() => import('@/components/pages/FygrBecomesOkimia.vue')),
  },
  {
    path: '/signin',
    name: 'signin',
    component: lazy(() => import('@/components/pages/SignIn.vue')),
    meta: {layout: 'no-auth'},
    children: [
      {
        path: '/signin/callback',
        name: 'signin-callback',
        component: lazy(() => import('@/components/pages/SignIn.vue')),
        meta: {layout: 'no-auth'},
      } as RouteRecordRaw,
    ],
  },
  {
    path: '/signup-en',
    name: 'signup-en',
    meta: {layout: 'no-auth'},
    component: lazy(() => import('@/components/pages/SignUp.vue')),
  },
  {
    path: '/signup',
    name: 'signup',
    meta: {layout: 'no-auth'},
    component: lazy(() => import('@/components/pages/SignUp.vue')),
    children: [
      {
        path: '/signup/callback',
        name: 'signup-callback',
      } as RouteRecordRaw,
      {
        path: '/signup/cpa/callback',
        name: 'signup-cpa-callback',
      } as RouteRecordRaw,
    ],
  },
  {
    path: '/personal-infos',
    name: 'personal-infos',
    meta: {layout: 'no-auth'},
    component: lazy(() => import('@/components/pages/PersonalInfos.vue')),
  },
  {
    path: '/company-infos',
    name: 'company-infos',
    meta: {layout: 'no-auth'},
    component: lazy(() => import('@/components/pages/CompanyInfos.vue')),
  },
  {
    path: '/personal-priorities',
    name: 'personal-priorities',
    meta: {layout: 'no-auth'},
    component: lazy(
      () => import('@/components/pages/OnboardingPriorities.vue'),
    ),
  },
  {
    path: '/personal-demo',
    name: 'personal-demo',
    meta: {layout: 'no-auth'},
    component: lazy(() => import('@/components/pages/OnboardingBookDemo.vue')),
  },
  {
    path: '/personal-data-sources',
    name: 'personal-data-sources',
    meta: {layout: 'no-auth'},
    component: lazy(
      () => import('@/components/pages/OnboardingDataSources.vue'),
    ),
  },
  {
    path: '/personal-data-sources/bank',
    name: 'personal-data-sources-bank',
    meta: {layout: 'no-auth', view: 'addBank'},
    component: lazy(
      () => import('@/components/pages/OnboardingDataSources.vue'),
    ),
  },
  {
    path: '/personal-data-sources/integration',
    name: 'personal-data-sources-integration',
    meta: {layout: 'no-auth', view: 'integration'},
    component: lazy(
      () => import('@/components/pages/OnboardingDataSources.vue'),
    ),
  },
  {
    path: '/partner/integration',
    name: 'partner-integration',
    meta: {layout: 'no-auth', permissions: ['bank_management']},
    component: lazy(
      () =>
        import('@/components/pages/PartnerIntegration/IntegratingPartnerDynamic.vue'),
    ),
  },
  {
    path: '/partner/manual-flow',
    name: 'partner-manual-flow',
    meta: {layout: 'no-auth', permissions: ['bank_management']},
    component: lazy(
      () =>
        import('@/components/pages/PartnerIntegration/PartnerManualFlow.vue'),
    ),
  },
  {
    path: '/signup-cpa',
    name: 'signup-cpa',
    meta: {layout: 'no-auth'},
    component: lazy(() => import('@/components/pages/SignUp.vue')),
  },
  {
    path: '/signup/libeo',
    name: 'signup-libeo',
    meta: {layout: 'no-auth'},
    component: lazy(() => import('@/components/pages/SignUp.vue')),
  },
  {
    path: '/signup/axonaut',
    name: 'signup-axonaut',
    meta: {layout: 'no-auth'},
    component: lazy(() => import('@/components/pages/SignUp.vue')),
  },
  {
    path: '/signup/odoo',
    name: 'signup-odoo',
    meta: {layout: 'no-auth'},
    component: lazy(() => import('@/components/pages/SignUp.vue')),
  },
  {
    path: '/signup/evoliz',
    name: 'signup-evoliz',
    meta: {layout: 'no-auth'},
    component: lazy(() => import('@/components/pages/SignUp.vue')),
  },
  {
    path: '/signup/pennylane',
    name: 'signup-pennylane',
    meta: {layout: 'no-auth'},
    component: lazy(() => import('@/components/pages/SignUp.vue')),
  },
  {
    path: '/signup/progbat',
    name: 'signup-progbat',
    meta: {layout: 'no-auth'},
    component: lazy(() => import('@/components/pages/SignUp.vue')),
  },
  {
    path: '/signup/partner/:partner_name',
    name: 'signup-partner',
    meta: {layout: 'no-auth'},
    component: lazy(() => import('@/components/pages/SignUp.vue')),
  },
  {
    path: '/signup-cpa-rca',
    name: 'signup-cpa-rca',
    meta: {layout: 'no-auth'},
    component: lazy(() => import('@/components/pages/SignUp.vue')),
  },
  {
    path: '/passwordrecovery',
    name: 'recovery',
    meta: {layout: 'no-auth'},
    component: lazy(() => import('@/components/pages/PasswordRecovery.vue')),
  },
  {
    path: '/completeSignUp',
    name: 'completeSignUp',
    component: lazy(() => import('@/components/pages/SignUp.vue')),
  },
  {
    path: '/console',
    name: 'console',
    component: lazy(() => import('@/components/pages/Console.vue')),
  },
  {
    path: '/dashboard',
    name: 'dashboard',
    component: lazy(() => import('@/components/pages/Dashboard.vue')),
  },
  {
    path: '/guide',
    name: 'guide',
    component: lazy(() => import('@/components/pages/GuidePage.vue')),
  },
  {
    path: '/invoices/:tab?',
    name: 'invoices',
    component: lazy(() => import('@/components/pages/Invoices.vue')),
  },
  {
    path: '/checks',
    name: 'checks',
    component: lazy(() => import('@/components/pages/Checks.vue')),
  },
  {
    path: '/transactions/:cardId?',
    name: 'transactions',
    component: lazy(() => import('@/components/pages/Transactions.vue')),
  },
  {
    path: '/categories',
    name: 'categories',
    component: lazy(() => import('@/components/pages/Categories.vue')),
  },
  {
    path: '/profile',
    name: 'profile',
    component: lazy(() => import('@/components/pages/Profile.vue')),
  },
  {
    path: '/company',
    name: 'company',
    component: lazy(() => import('@/components/pages/Company.vue')),
  },
  {
    path: '/admin',
    name: 'admin',
    component: lazy(() => import('@/components/pages/AdminWrapper.vue')),
  },
  {
    path: '/users',
    name: 'users',
    component: lazy(() => import('@/components/pages/CompanyUsers.vue')),
  },
  {
    path: '/callback',
    name: 'callback',
    component: lazy(() => import('@/components/pages/BankConfirmation.vue')),
  },
  {
    path: '/acceptInvite',
    name: 'acceptInvite',
    meta: {layout: 'no-auth'},
    component: lazy(() => import('@/components/pages/AcceptInvite.vue')),
  },
  {
    path: '/cabinet',
    name: 'cabinet',
    component: lazy(() => import('@/components/pages/Company.vue')),
  },
  {
    path: '/onboarding',
    name: 'onboarding',
    redirect: {name: 'onboarding-welcome'},
    meta: {layout: 'no-auth'},
    component: lazy(
      () =>
        import('@/components/pages/NoBanksOnboarding/NoBanksOnboarding.vue'),
    ),
    children: [
      {
        path: 'welcome',
        meta: {layout: 'no-auth'},
        name: 'onboarding-welcome',
        component: lazy(
          () =>
            import('@/components/pages/NoBanksOnboarding/OnboardingWelcome.vue'),
        ),
      },
      // Temporary: these sub-pages are superseded by the redesigned welcome
      // page. Redirect them to welcome (names kept so existing navigation still
      // resolves) until they are reworked or removed.
      {
        path: 'why-connect',
        meta: {layout: 'no-auth'},
        name: 'onboarding-why-connect',
        redirect: {name: 'onboarding-welcome'},
      },
      {
        path: 'add-bank',
        meta: {layout: 'no-auth'},
        name: 'onboarding-add-bank',
        redirect: {name: 'onboarding-welcome'},
      },
      {
        path: 'book-a-demo',
        meta: {layout: 'no-auth'},
        name: 'onboarding-book-a-demo',
        redirect: {name: 'onboarding-welcome'},
      },
    ],
  },
  {
    path: '/treasury-investment-long',
    name: 'treasury-investment-long',
    redirect: {name: 'treasury-investment-long-profile'},
    meta: {layout: 'no-auth'},
    component: lazy(
      () => import('@/components/pages/TreasuryInvestmentLong.vue'),
    ),
    children: [
      {
        path: 'profile',
        name: 'treasury-investment-long-profile',
        meta: {step: 0},
        component: lazy(
          () => import('@/components/pages/TreasuryInvestmentLong/Profile.vue'),
        ),
      },
      {
        path: 'result',
        name: 'treasury-investment-long-result',
        meta: {step: 1},
        component: lazy(
          () => import('@/components/pages/TreasuryInvestmentLong/Results.vue'),
        ),
      },
    ],
  },
  {
    path: '/treasury-investment',
    name: 'treasury-investment',
    redirect: {name: 'treasury-investment-intro'},
    component: lazy(() => import('@/components/pages/TreasuryInvestment.vue')),
    children: [
      {
        path: 'intro',
        name: 'treasury-investment-intro',
        meta: {step: 0},
        component: lazy(
          () => import('@/components/pages/TreasuryInvestment/Intro.vue'),
        ),
      },
      {
        path: 'profile',
        name: 'treasury-investment-profile',
        meta: {step: 1},
        component: lazy(
          () => import('@/components/pages/TreasuryInvestment/Profile.vue'),
        ),
      },
      {
        path: 'rules',
        name: 'treasury-investment-rules',
        meta: {step: 2},
        component: lazy(
          () => import('@/components/pages/TreasuryInvestment/Rules.vue'),
        ),
      },
      {
        path: 'personal-info',
        name: 'treasury-investment-personal-info',
        meta: {step: 3},
        component: lazy(
          () =>
            import('@/components/pages/TreasuryInvestment/PersonalInfo.vue'),
        ),
      },
      {
        path: 'company-info',
        name: 'treasury-investment-company-info',
        meta: {step: 3},
        component: lazy(
          () => import('@/components/pages/TreasuryInvestment/CompanyInfo.vue'),
        ),
      },
      {
        path: 'documents',
        name: 'treasury-investment-documents',
        meta: {step: 4},
        component: lazy(
          () => import('@/components/pages/TreasuryInvestment/Documents.vue'),
        ),
      },
      {
        path: 'final/:type?',
        name: 'treasury-investment-final',
        meta: {step: 5},
        component: lazy(
          () => import('@/components/pages/TreasuryInvestment/Final.vue'),
        ),
      },
    ],
  },
  {
    path: '/my-investments',
    name: 'my-investments',
    redirect: {name: 'my-investments-home'},
    meta: {layout: 'no-auth'},
    component: lazy(() => import('@/components/pages/MyInvestments.vue')),
    children: [
      {
        path: 'home',
        name: 'my-investments-home',
        component: lazy(
          () =>
            import('@/components/pages/MyInvestments/MyInvestmentsTable.vue'),
        ),
      },
      {
        path: 'invest/:currency/:shareClassId/:idPayment?/:flow?',
        name: 'my-investments-invest',
        component: lazy(
          () => import('@/components/pages/MyInvestments/Invest.vue'),
        ),
      },
      {
        path: 'withdraw/:currency/:shareClassId?',
        name: 'my-investments-withdraw',
        component: lazy(
          () => import('@/components/pages/MyInvestments/Withdraw.vue'),
        ),
      },
    ],
  },
  {
    path: '/analytics',
    name: 'analytics',
    component: lazy(() => import('@/components/pages/Analytics.vue')),
  },
  {
    path: '/partner/selection',
    name: 'partner-selection',
    meta: {layout: 'default', permissions: ['bank_management']},
    component: lazy(
      () =>
        import('@/components/pages/PartnerIntegration/ChoosePartnerIntegration.vue'),
    ),
  },
  {
    path: '/partner/callback',
    name: 'partner-callback',
    meta: {layout: 'no-auth', permissions: ['bank_management']},
    component: lazy(
      () =>
        import('@/components/pages/PartnerIntegration/ChoosePartnerIntegration.vue'),
    ),
  },
  {
    path: '/no-permissions',
    name: 'no-permissions',
    component: lazy(() => import('@/components/pages/NoPermissions.vue')),
    meta: {layout: 'no-auth'},
  },
  {
    path: '/book-meeting/:type?',
    name: 'book-a-meeting',
    component: lazy(() => import('@/components/pages/BookMeeting.vue')),
    meta: {layout: 'no-auth'},
  },
  {
    path: '/book-a-meeting-analytics',
    name: 'book-a-meeting-analytics',
    component: lazy(
      () => import('@/components/elements/Analytics/Reports/HelpMeeting.vue'),
    ),
  },

  {
    path: '/:pathMatch(.*)*',
    redirect: '/dashboard',
  },
  {
    path: '/transfer/callback',
    name: 'transfer-callback',
    meta: {layout: 'no-auth'},
    component: lazy(() => import('@/components/pages/TransferSummary.vue')),
  },
  {
    path: '/bank-transfers',
    name: 'bank-transfers',
    meta: {layout: 'no-auth'},
    component: lazy(
      () => import('@/components/pages/BankTransfers/BankTransfers.vue'),
    ),
  },
  {
    path: '/bank-transfers/callback',
    name: 'bank-transfers-callback',
    meta: {layout: 'no-auth'},
    component: lazy(
      () =>
        import('@/components/pages/BankTransfers/BankTransfersCallback.vue'),
    ),
  },
  {
    path: '/bank-transfers/company-info',
    name: 'bank-transfers-company',
    meta: {layout: 'no-auth'},
    component: lazy(
      () =>
        import('@/components/pages/BankTransfers/BankTransfersCompanyInfo.vue'),
    ),
  },
  {
    path: '/maintenance',
    name: 'maintenance',
    meta: {layout: 'no-auth'},
    component: () => import('@/components/pages/MaintenanceView.vue'),
  },
  {
    path: '/sconnect',
    name: 'sconnect',
    component: lazy(() => import('@/components/pages/SConnectTest.vue')),
  },
  {
    path: '/validate-operations',
    name: 'validate-operations',
    meta: {
      layout: 'no-auth',
      permissions: [
        'admin',
        'bank_management',
        'manage_own_payment_cards',
        'manage_all_payment_cards',
      ],
    },
    component: () => import('@/components/pages/ValidateOperations.vue'),
  },
  {
    path: '/payments',
    name: 'banking-payments',
    redirect: {name: 'banking-payments-validate'},
    component: lazy(() => import('@/components/pages/Payments.vue')),
    children: [
      {
        path: 'validate',
        name: 'banking-payments-validate',
        component: lazy(
          () => import('@/components/pages/Payments/Validate.vue'),
        ),
        meta: {
          companyPermissions: ['embedded_banking_enabled'],
          permissions: ['write_payment', 'read_payment'],
        },
      },
      {
        path: 'ongoing',
        name: 'banking-payments-ongoing',
        component: lazy(
          () => import('@/components/pages/Payments/Ongoing.vue'),
        ),
        meta: {
          companyPermissions: ['embedded_banking_enabled'],
          permissions: ['write_payment', 'read_payment'],
        },
      },
      {
        path: 'passed',
        name: 'banking-payments-passed',
        component: lazy(() => import('@/components/pages/Payments/Passed.vue')),
        meta: {
          companyPermissions: ['embedded_banking_enabled'],
          permissions: ['write_payment', 'read_payment'],
        },
      },
      {
        path: 'beneficiaries',
        name: 'banking-payments-beneficiaries',
        component: lazy(
          () => import('@/components/pages/Payments/Beneficiaries.vue'),
        ),
      },
    ],
  },
  {
    path: '/banking-onboarding',
    name: 'banking-onboarding',
    redirect: {name: 'banking-onboarding-intro'},
    meta: {
      layout: 'default',
      companyPermissions: ['embedded_banking_enabled'],
      permissions: ['write_payment'],
    },
    component: lazy(() => import('@/components/pages/BankingOnboarding.vue')),
    children: [
      {
        path: 'intro',
        name: 'banking-onboarding-intro',
        meta: {step: 0},
        component: lazy(
          () => import('@/components/pages/BankingOnboarding/Intro.vue'),
        ),
      },
      {
        path: 'company',
        name: 'banking-onboarding-company',
        meta: {step: 1},
        component: lazy(
          () => import('@/components/pages/BankingOnboarding/Company.vue'),
        ),
      },
      {
        path: 'beneficiary',
        name: 'banking-onboarding-beneficiary',
        meta: {step: 2},
        component: lazy(
          () => import('@/components/pages/BankingOnboarding/Beneficiary.vue'),
        ),
      },
      {
        path: 'documents',
        name: 'banking-onboarding-documents',
        meta: {step: 3},
        component: lazy(
          () => import('@/components/pages/BankingOnboarding/Documents.vue'),
        ),
      },
      {
        path: 'verification',
        name: 'banking-onboarding-verification',
        meta: {step: 4},
        component: lazy(
          () =>
            import('@/components/pages/BankingOnboarding/VerificationInProgress.vue'),
        ),
      },
      {
        path: 'complete',
        name: 'banking-onboarding-complete',
        meta: {step: 5},
        component: lazy(
          () =>
            import('@/components/pages/BankingOnboarding/FinalValidation.vue'),
        ),
      },
      {
        path: 'additional-docs',
        name: 'banking-onboarding-additional-docs',
        meta: {step: 5},
        component: lazy(
          () =>
            import('@/components/pages/BankingOnboarding/AdditionalDocuments.vue'),
        ),
      },
    ],
  },
  {
    path: '/e-banking',
    name: 'e-banking',
    redirect: {name: 'e-banking-accounts'},
    meta: {
      companyPermissions: ['embedded_banking_enabled'],
    },
    component: lazy(() => import('@/components/pages/BankingRoot.vue')),
    children: [
      {
        path: 'accounts',
        name: 'e-banking-accounts',
        component: lazy(
          () => import('@/components/pages/e-banking/Accounts.vue'),
        ),
      },
      {
        path: 'cards',
        name: 'e-banking-cards',
        component: lazy(() => import('@/components/pages/e-banking/Cards.vue')),
        redirect: {name: 'e-banking-cards-list'},
        children: [
          {
            path: 'list',
            name: 'e-banking-cards-list',
            component: lazy(
              () => import('@/components/pages/e-banking/Cards/List.vue'),
            ),
          },
          {
            path: 'create',
            name: 'e-banking-cards-create',
            component: lazy(
              () => import('@/components/pages/e-banking/Cards/Create.vue'),
            ),
          },
          {
            path: 'card/:cardId',
            name: 'e-banking-cards-card-id',
            component: lazy(
              () =>
                import('@/components/pages/e-banking/Cards/CardMainPage.vue'),
            ),
          },
          {
            path: 'card/:cardId/set-pin',
            name: 'e-banking-cards-set-pin',
            component: lazy(
              () => import('@/components/pages/e-banking/Cards/SetPin.vue'),
            ),
          },
          {
            path: 'card/:cardId/block',
            name: 'e-banking-cards-block',
            component: lazy(
              () =>
                import('@/components/pages/e-banking/Cards/BlockUnblockCard.vue'),
            ),
          },
          {
            path: 'card/:cardId/unblock',
            name: 'e-banking-cards-unblock',
            component: lazy(
              () =>
                import('@/components/pages/e-banking/Cards/BlockUnblockCard.vue'),
            ),
          },
          {
            path: 'card/:cardId/declare-incident',
            name: 'e-banking-cards-declare-incident',
            component: lazy(
              () =>
                import('@/components/pages/e-banking/Cards/DeclareIncident.vue'),
            ),
          },
          {
            path: 'card/:cardId/spending-limits',
            name: 'e-banking-cards-spending-limits',
            component: lazy(
              () =>
                import('@/components/pages/e-banking/Cards/SpendingLimits.vue'),
            ),
          },
          {
            path: 'card/:cardId/notified-user',
            name: 'e-banking-cards-notified-user',
            component: lazy(
              () =>
                import('@/components/pages/e-banking/Cards/CardNotifiedUser.vue'),
            ),
          },
          {
            path: 'card/:cardId/delete',
            name: 'e-banking-cards-delete',
            component: lazy(
              () => import('@/components/pages/e-banking/Cards/CardDelete.vue'),
            ),
          },
        ],
      },
      {
        path: 'documents',
        name: 'e-banking-documents',
        component: lazy(
          () => import('@/components/pages/e-banking/Documents.vue'),
        ),
        children: [
          {
            path: 'general-conditions',
            name: 'e-banking-documents-general-conditions',
            component: lazy(
              () =>
                import('@/components/pages/e-banking/Documents/GeneralConditions.vue'),
            ),
          },
          {
            path: 'company-information',
            name: 'e-banking-documents-company-information',
            component: lazy(
              () =>
                import('@/components/pages/e-banking/Documents/Company.vue'),
            ),
          },
          {
            path: 'beneficiaries',
            name: 'e-banking-documents-beneficiaries',
            component: lazy(
              () =>
                import('@/components/pages/e-banking/Documents/Beneficiary.vue'),
            ),
          },
          {
            path: 'administrative-documents',
            name: 'e-banking-documents-administrative-documents',
            component: lazy(
              () => import('@/components/pages/e-banking/Documents/Docs.vue'),
            ),
          },
        ],
      },
    ],
  },
] satisfies RouteRecordRaw[];

export default routes;
